import React from 'react';

import { useDownloadJournalFile } from '@mutations/journalFile';
import { useDownloadBatchReport, useDownloadBatchZipReport } from '../mutations/batchReports';
import { downloadResponseFile, downloadResponseZipFile } from '../../menu/jobsMenu/downloadResponseFile';

import { isExportTypeJob, shouldDownloadJobAsZip } from '../../menu/jobsMenu/helpers';
import { Job } from '../../menu/jobsMenu/types';

import Spinner from '../spinner.component';

type JimJobErrorAlertProps = {
  showErrors: (errors: Job['returnedError']) => void,
  title: string,
  errors: Job['returnedError'],
}

export const JimJobErrorAlert: React.FC<JimJobErrorAlertProps> = (
  { showErrors, errors, title }: JimJobErrorAlertProps
) => (
  <div className="trial-balance-error-alert">
    <span>{title} </span>
    <span
      className="trial-balance-error-link"
      onClick={() => showErrors(errors)} // eslint-disable-line react/jsx-no-bind
    >click here to see details
    </span>
  </div>
);


type JimJobExportSuccessAlertProps = {
  title: string,
  job: Job,
  isZipFile?: boolean,
}

const JimJobExportSuccessAlert: React.FC<JimJobExportSuccessAlertProps> = (
  { title, job, isZipFile }: JimJobExportSuccessAlertProps
) => {
  const jobLabel = job.returnedValue?.filename || job.jobType;
  const jobType = job.jobType;
  const shouldDownloadAsZip = shouldDownloadJobAsZip(job, isZipFile);

  const { mutate: downloadReport, isLoading }
    = (jobType === 'generate-journal-file')
      ? useDownloadJournalFile(downloadResponseFile(jobLabel))
      : useDownloadBatchReport(shouldDownloadAsZip
        ? downloadResponseZipFile(jobLabel)
        : downloadResponseFile(jobLabel));

  const { mutate: downloadBatchZipReport, isLoading: isLoadingZip }
    = useDownloadBatchZipReport(downloadResponseZipFile(jobLabel));

  const downloadExportedFile = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (isZipFile) {
      downloadBatchZipReport({ fileName: job.returnedValue?.filename });
    } else {
      downloadReport({
        jobId: job.jobId,
        exportFormat: job.returnedValue?.exportFormat,
      });
    }
  };

  return (
    <div className="d-flex align-items-center">
      <span>{title}</span>
      {
        (isLoading || isLoadingZip)
          ? (
            <>
              <Spinner className="mx-2 d-flex spinner-no-margin" small />
              Downloading
            </>
          )
          : (
            <a
              href="#"
              className="d-inline-block ml-2"
              onClick={downloadExportedFile} // eslint-disable-line react/jsx-no-bind
            >
              Download exported file
            </a>
          )
      }
    </div>
  );
};

type JimJobPlainSuccessAlertProps = {
  title: string
}

const JimJobPlainSuccessAlert: React.FC<JimJobPlainSuccessAlertProps> = ({ title }: JimJobPlainSuccessAlertProps) => (
  <div>
    <span>{title}</span>
  </div>
);


type JimJobSuccessAlertProps = {
  title: string,
  job: Job,
  isArchive?: boolean,
}

export const JimJobSuccessAlert: React.FC<JimJobSuccessAlertProps> = ({
  title,
  job,
  isArchive,
}: JimJobSuccessAlertProps) =>
  isExportTypeJob(job)
    ? (
      <JimJobExportSuccessAlert
        title={title}
        job={job}
        isZipFile={isArchive}
      />
    )
    : (
      <JimJobPlainSuccessAlert
        title={title}
      />
    );
